import './styles/tailwind.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { DatadogErrorBoundary } from '@my-medication/shared-components';
import { safeLocalStorage } from '@my-medication/shared-components/helpers/safeStorage';
import { setDefaultStorage } from '@my-medication/shared-components/hooks/useBffApi';

import { recoverFromChunkError } from '@helpers/chunkRecovery';
import { AppHydrateFallback } from '@router/AppHydrateFallback';
import { AppRouteError } from '@router/AppRouteError';
import { RootErrorBoundary } from '@router/RootErrorBoundary';
import { routes } from '@router/routes';
import { initDatadog, reportDatadogError, startSessionReplayRecording } from '@services/datadog';

import { App } from './App';

// The BFF client keeps no platform defaults of its own, so the browser storage
// area is registered here, before anything can call useBffApi.
setDefaultStorage(safeLocalStorage);

if (document.readyState === 'complete') {
  initDatadog();
} else {
  window.addEventListener('load', () => initDatadog(), { once: true });
}

window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  if (recoverFromChunkError(event.reason, 'unhandledrejection')) return;

  reportDatadogError(event.reason);
  startSessionReplayRecording();
});

window.addEventListener('vite:preloadError', (event: Event & { payload?: unknown }) => {
  recoverFromChunkError(event.payload, 'vite:preloadError', { trusted: true });
});

const router = createBrowserRouter([
  {
    element: <App />,
    errorElement: <RootErrorBoundary />,
    HydrateFallback: AppHydrateFallback,
    children: [{ id: 'app-routes', errorElement: <AppRouteError />, children: routes }],
  },
]);

createRoot(document.getElementById('app') as HTMLElement).render(
  <StrictMode>
    <DatadogErrorBoundary boundary="webadmin-root">
      <RouterProvider router={router} />
    </DatadogErrorBoundary>
  </StrictMode>,
);
